<template>
  <div
    ref="cardRef"
    class="bg-white dark:bg-(--p-neutral97) border border-(--p-content-border-color) rounded-lg overflow-hidden flex flex-col"
  >
    <div
      class="w-full cursor-pointer flex-shrink-0 relative h-[160px]"
      @click="handleMarketClick"
      role="button"
      tabindex="0"
      :aria-label="$t('viewDetailsForMarket', { name: market.name })"
      @keydown.enter="handleMarketClick"
      @keydown.space="handleMarketClick"
    >
      <div v-show="imageLoaded" class="p-4 z-8 absolute justify-between flex flex-col h-full w-full">
        <div class="flex items-center justify-between">
          <ExchangeAvatarGroup :exchanges="market.exchanges" :exchange="market.exchange" />
        </div>
        <div>
          <div
            v-if="showConditional"
            v-tooltip.top="{ value: $t('conditionalTooltip'), class: 'question-tooltip' }"
            class="flex items-center gap-1 mb-1 cursor-help w-fit"
          >
            <ConditionalIcon class="size-[18px] text-white" />
            <span class="text-white text-xs uppercase leading-4 [text-shadow:0_1px_6px_rgba(0,0,0,0.4)]">{{
              $t('conditional')
            }}</span>
          </div>
          <p class="text-white text-xl font-semibold leading-[22px]">{{ market.name }}</p>
          <span
            v-if="market.restricted"
            class="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold text-white bg-black rounded-full leading-tight"
            >{{ $t('restricted') }}</span
          >
        </div>
      </div>
      <div v-show="imageLoaded" class="gradient-div absolute h-full w-full z-5"></div>
      <div class="absolute h-full w-full z-0 bg-(--p-surface-200) dark:bg-(--p-surface-700)">
        <LazyImage
          class="w-full h-full object-cover"
          :src="imageUrl"
          :title="market.name"
          fetchpriority="high"
          @load="imgLoadFired = true"
        />
      </div>
    </div>
    <div class="flex flex-col h-[184px]">
      <template v-if="displayedStrikes.length > 1">
        <StrikeCardRow
          v-for="(strike, idx) in displayedStrikes"
          :key="`${strike.conid_yes}-${strike.conid_no}`"
          :strike="strike"
          :exchange="strike.exchange || market.exchange"
          :multi-exchange="(market.exchanges?.length ?? 0) > 1"
          :productConid="routingForExchange(strike.exchange || market.exchange).conid"
          :routingExchange="routingForExchange(strike.exchange || market.exchange).exchange"
          :origin="origin"
          :priceSide="priceSide"
          :focusStrikeOnClick="focusStrikeOnClick"
          :isLast="idx === displayedStrikes.length - 1"
        ></StrikeCardRow>
      </template>
      <template v-else-if="displayedStrikes.length === 1">
        <SingleStrikeCardRow
          :strike="displayedStrikes[0]"
          :exchange="displayedStrikes[0].exchange || market.exchange"
          :multi-exchange="(market.exchanges?.length ?? 0) > 1"
          :productConid="routingForExchange(displayedStrikes[0].exchange || market.exchange).conid"
          :routingExchange="routingForExchange(displayedStrikes[0].exchange || market.exchange).exchange"
          :origin="origin"
          :priceSide="priceSide"
          :focusStrikeOnClick="focusStrikeOnClick"
        />
      </template>
      <div v-else class="flex-1 flex items-center justify-center text-muted-color text-sm min-h-[200px]">
        {{ $t('noContractsAvailable', 'No contracts available') }}
      </div>
      <div class="mt-auto">
        <div
          :class="[
            'px-4 h-[40px] flex items-center justify-between',
            hasMoreStrikes &&
              'cursor-pointer hover:bg-(--p-surface-50) dark:hover:bg-(--p-surface-800) transition-colors',
          ]"
          @click="hasMoreStrikes && handleViewMoreClick()"
        >
          <Button
            v-if="hasMoreStrikes"
            v-slot="slotProps"
            asChild
            :aria-label="$t('viewMoreContractsForMarket', { count: remainingStrikesCount, name: market.name })"
          >
            <p v-bind="slotProps.a11yAttrs" class="text-on-surface text-[12px]">
              {{ $t('seeMoreContracts', { count: remainingStrikesCount }, remainingStrikesCount) }}
            </p>
          </Button>
          <span v-else class="text-sm">&nbsp;</span>
          <p v-if="formattedOI" class="text-muted-color text-[12px]">
            {{ formattedOI }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import LazyImage from './LazyImage.vue';
import type { ForecastTraderDetailedMarket } from '@/types/forecasttrader.types';
import ExchangeAvatarGroup from './ExchangeAvatarGroup.vue';
import StrikeCardRow from './StrikeCardRow.vue';
import SingleStrikeCardRow from './SingleStrikeCardRow.vue';
import ConditionalIcon from '@/icons/ConditionalIcon.vue';
import { useConditionals } from '@/composables/useConditionals';
import { useAppState } from '@/stores/useAppState';
import { formatOpenInterest } from '@/utils/formatNumber';
import useContractActions from '@/composables/useContractActions';
import { useProductHub } from '@/composables/useProductHub';
import { forecastIconUrl, preferredProductConid } from '@/utils/forecastIcon';
import { useImageReady } from '@/composables/useImageReady';

const appState = useAppState();
const { isProductHubEnabled } = useProductHub();
const { isConditionalsEnabled } = useConditionals();
const actions = useContractActions();

const cardRef = shallowRef<HTMLElement>();

export interface MarketCardProps {
  market: ForecastTraderDetailedMarket;
  limit?: number;
  size: 'large' | 'small';
  origin?: string;
  openExternalContracts?: boolean;
  priceSide?: 'bid' | 'ask';
  focusStrikeOnClick?: boolean;
}

export interface MarketCardEmits {
  'view-more': [];
}

const props = withDefaults(defineProps<MarketCardProps>(), {
  limit: 2,
  origin: 'landing',
});

defineEmits<MarketCardEmits>();

const imageUrl = computed(() =>
  forecastIconUrl({
    conid: props.market.conid,
    productConid:
      preferredProductConid(props.market.exchanges, props.market.productConids) ?? props.market.product_conid,
    category: props.market.logo_category,
  }),
);

const preloaded = useImageReady(imageUrl);
const imgLoadFired = shallowRef(false);
const imageLoaded = computed(() => preloaded.value || imgLoadFired.value);

const showConditional = computed(() => isConditionalsEnabled.value && !!props.market.isConditional);

const displayedStrikes = computed(() => {
  return (props.market.strikes || []).slice(0, props.limit);
});

const hasMoreStrikes = computed(() => {
  return props.market.strike_count > props.limit!;
});

const remainingStrikesCount = computed(() => {
  return props.market.strike_count - displayedStrikes.value.length;
});

const formattedOI = computed(() => {
  if (props.market.open_interest == null) return null;
  return formatOpenInterest(String(props.market.open_interest));
});

function routingForExchange(exchange: string | undefined): { conid: number; exchange: string } {
  if (exchange && props.market.exchanges && props.market.productConids) {
    const idx = props.market.exchanges.indexOf(exchange);
    if (idx !== -1 && props.market.productConids[idx] != null) {
      return { conid: props.market.productConids[idx]!, exchange };
    }
  }
  return { conid: props.market.conid, exchange: props.market.exchange };
}

const isUnderlying = computed(
  () => !isProductHubEnabled.value && !!props.market.product_conid && props.market.product_conid !== props.market.conid,
);

const handleMarketClick = () => {
  if (appState.isScrolling) {
    return;
  }
  window.scrollTo(0, 0);
  window.$apm?.logUserInteractionEvent('Card Click', 'market-click', { origin: props.origin }, false);
  actions.openQuoteDetails(props.market.conid, props.market.exchange);
};

const handleViewMoreClick = () => {
  if (appState.isScrolling) {
    return;
  }
  window.scrollTo(0, 0);
  window.$apm?.logUserInteractionEvent('Card Click', 'view-more', { origin: props.origin }, false);
  actions.openContractsList(props.market.conid, props.market.exchange, { asUnderlying: isUnderlying.value });
};
</script>

<style scoped>
.gradient-div {
  background: linear-gradient(180deg, rgb(0 0 0 / 20%) 0%, rgb(0 0 0 / 80%) 75%);
}
</style>
<!-- last modified by AI tools on 2026-10-13 -->
